<template>
  <div class="entity-list-page growth-contacts">
    <header class="list-header">
      <div>
        <router-link to="/settings/marketing" class="back-link">← Marketing</router-link>
        <h1>Growth contacts</h1>
        <p class="list-sub">
          Prospect and agent contacts for outreach across Praetorian products.
        </p>
      </div>
    </header>

      <div class="filters-bar">
        <input
          v-model="emailFilter"
          type="text"
          placeholder="Filter by email..."
          @input="debouncedLoad"
        />
        <select v-model="statusFilter" @change="resetAndLoad">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select v-model="stageFilter" @change="resetAndLoad">
          <option value="">All stages</option>
          <option value="prospect">Prospect</option>
          <option value="engaged">Engaged</option>
          <option value="customer">Customer</option>
        </select>
        <button class="btn-refresh" :disabled="loading" @click="loadContacts">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </button>
      </div>

      <div v-if="loading && contacts.length === 0" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Loading contacts...
      </div>

      <div v-else-if="error" class="error-state">{{ error }}</div>

      <div v-else class="table-container">
        <div class="table-header">
          <span>{{ total }} contact(s)</span>
        </div>
        <table class="contacts-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Agency</th>
              <th>Source</th>
              <th>Status</th>
              <th>Stage</th>
              <th>Last Activity</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="contact in contacts" :key="contact.id" class="click-row" @click="openProfile(contact)">
              <td>{{ contact.name || '—' }}</td>
              <td>{{ contact.email }}</td>
              <td>{{ contact.agency_name || '—' }}</td>
              <td>{{ contact.source || '—' }}</td>
              <td>{{ contact.status }}</td>
              <td>{{ contact.lifecycle_stage || '—' }}</td>
              <td>{{ formatDate(contact.last_activity_at) }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="hasMore || offset > 0" class="pagination">
          <button :disabled="loading || offset === 0" @click="prevPage">Previous</button>
          <span>{{ offset + 1 }}–{{ offset + contacts.length }} of {{ total }}</span>
          <button :disabled="loading || !hasMore" @click="nextPage">Next</button>
        </div>
      </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { debounce } from 'lodash-es'
import { UI_CONSTANTS } from '@/constants'
import api from '@/services/api'

const router = useRouter()

const loading = ref(false)
const error = ref(null)
const contacts = ref([])
const total = ref(0)
const offset = ref(0)
const limit = ref(UI_CONSTANTS.DEFAULT_PAGE_SIZE)
const hasMore = ref(false)
const emailFilter = ref('')
const statusFilter = ref('')
const stageFilter = ref('')

async function loadContacts() {
  loading.value = true
  error.value = null
  try {
    const params = new URLSearchParams({
      limit: String(limit.value),
      offset: String(offset.value),
    })
    if (emailFilter.value) params.append('email', emailFilter.value)
    if (statusFilter.value) params.append('status', statusFilter.value)
    if (stageFilter.value) params.append('lifecycle_stage', stageFilter.value)

    const response = await api.get(`/growth-contacts/?${params}`)
    contacts.value = response.data?.results || []
    total.value = response.data?.total ?? contacts.value.length
    hasMore.value = offset.value + contacts.value.length < total.value
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load growth contacts'
  } finally {
    loading.value = false
  }
}

const debouncedLoad = debounce(() => {
  offset.value = 0
  loadContacts()
}, UI_CONSTANTS.SEARCH_DEBOUNCE_MS)

function resetAndLoad() {
  offset.value = 0
  loadContacts()
}

function prevPage() {
  offset.value = Math.max(0, offset.value - limit.value)
  loadContacts()
}

function nextPage() {
  offset.value += limit.value
  loadContacts()
}

function openProfile(contact) {
  router.push(`/growth-contacts/${contact.id}`)
}

function formatDate(dateString) {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

onMounted(loadContacts)
</script>

<style scoped>
.growth-contacts {
  padding-bottom: 2.5rem;
}

.click-row {
  cursor: pointer;
}

.filters-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.filters-bar input,
.filters-bar select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
}

.btn-refresh {
  padding: 0.5rem 1rem;
  border: 1px solid var(--p-accent, #1e3a5f);
  border-radius: 8px;
  background: var(--p-accent, #1e3a5f);
  color: white;
  cursor: pointer;
}

.table-container {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem;
}

.table-header {
  margin-bottom: 1rem;
  color: #64748b;
  font-size: 0.875rem;
}

.contacts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.contacts-table th,
.contacts-table td {
  padding: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
}

.contacts-table th {
  background: #f8fafc;
  font-weight: 600;
}

.pagination {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 2rem;
  color: #64748b;
}
</style>
